/*
 * <pb-compare>: comparador antes/depois. A posição (0–100) vai para --pb-compare-pos.
 * Ponteiro (mouse/dedo) arrasta a divisória em qualquer ponto da imagem; o input range
 * escondido cuida do teclado e do leitor de tela.
 */
if (!customElements.get('pb-compare')) {
  customElements.define(
    'pb-compare',
    class PbCompare extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.pb-compare__stage');
        this.range = this.querySelector('.pb-compare__range');
        if (!this.stage || !this.range) return;
        this.onInput = () => this.set(Number(this.range.value));
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.range.addEventListener('input', this.onInput);
        this.stage.addEventListener('pointerdown', this.onDown);
        this.stage.addEventListener('pointermove', this.onMove);
        this.stage.addEventListener('pointerup', this.onUp);
        this.stage.addEventListener('pointercancel', this.onUp);
        this.set(Number(this.range.value));
      }

      disconnectedCallback() {
        this.range?.removeEventListener('input', this.onInput);
        this.stage?.removeEventListener('pointerdown', this.onDown);
        this.stage?.removeEventListener('pointermove', this.onMove);
        this.stage?.removeEventListener('pointerup', this.onUp);
        this.stage?.removeEventListener('pointercancel', this.onUp);
      }

      onDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.stage.setPointerCapture?.(event.pointerId);
        this.classList.add('is-dragging');
        this.fromPointer(event);
      }

      onMove(event) {
        if (this.dragging) this.fromPointer(event);
      }

      onUp() {
        this.dragging = false;
        this.classList.remove('is-dragging');
      }

      fromPointer(event) {
        const rect = this.stage.getBoundingClientRect();
        if (!rect.width) return;
        this.set(((event.clientX - rect.left) / rect.width) * 100);
      }

      set(value) {
        const pos = Math.min(100, Math.max(0, value));
        this.style.setProperty('--pb-compare-pos', `${pos}%`);
        this.range.value = String(Math.round(pos));
        const depois = 100 - Math.round(pos);
        this.range.setAttribute('aria-valuetext', `${Math.round(pos)}% antes, ${depois}% depois`);
      }
    }
  );
}

/*
 * <pb-antes-depois>: abas do comparador (uma por tom). Setas do teclado trocam de aba e,
 * na página do produto, a aba acompanha a variante escolhida no seletor (data-variant).
 */
if (!customElements.get('pb-antes-depois')) {
  customElements.define(
    'pb-antes-depois',
    class PbAntesDepois extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (this.tabs.length < 2) return;
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => this.onKey(event, tab));
        });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const id = data?.variant?.id;
            const tab = id && this.tabs.find((t) => t.dataset.variant === String(id));
            if (tab) this.select(tab, { reveal: true });
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      onKey(event, tab) {
        const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const i = this.tabs.indexOf(tab);
        const step = keys[event.key];
        let next;
        if (step === 'first') next = this.tabs[0];
        else if (step === 'last') next = this.tabs[this.tabs.length - 1];
        else next = this.tabs[(i + step + this.tabs.length) % this.tabs.length];
        this.select(next);
        next.focus();
      }

      select(tab, { reveal = false } = {}) {
        this.tabs.forEach((t) => {
          const active = t === tab;
          t.setAttribute('aria-selected', String(active));
          t.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        });
        // só rola a fileira de abas (nunca a página)
        const list = tab.parentElement;
        if (reveal && list && list.scrollWidth > list.clientWidth) {
          list.scrollTo({ left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
    }
  );
}
